import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { resolveItemImageUrl } from '../utils/tenantAssetUrls';
import {
  computeLineEstimate,
  lineEstimateLabel,
  formatMoney,
  isPackageMode,
  isBulkMode,
  computeBulkLineTotal,
  BULK_WEIGHT_STEP,
  unitLabel,
} from '../utils/weightEstimate';
import './Cart.css';

// Neutral inline placeholder (no file dependency, no tenant-specific image).
const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect width='120' height='120' fill='%23f0ece4'/%3E%3Ctext x='50%25' y='50%25' fill='%23a89f8d' font-family='sans-serif' font-size='11' text-anchor='middle' dominant-baseline='middle'%3ENo image%3C/text%3E%3C/svg%3E";

function Cart() {
  const { cartItems, updateQuantity, removeFromCart } = useCart();

  // Split into deterministic (each + bulk, charge-now) vs package (billed later).
  const deterministicItems = cartItems.filter(item => !isPackageMode(item));
  const packagedItems = cartItems.filter(isPackageMode);
  const hasDeterministic = deterministicItems.length > 0;
  const hasPackaged = packagedItems.length > 0;

  const lineTotal = (item) => {
    if (isBulkMode(item)) {
      return computeBulkLineTotal(item, item.quantity);
    }
    return Number(item.price) * item.quantity;
  };

  // Charge-now subtotal: each + bulk (exact weight × rate for bulk).
  const estimatedSubtotal = deterministicItems.reduce(
    (total, item) => total + (lineTotal(item) || 0), 0
  );

  const weighedEstimateTotal = packagedItems.reduce((sum, item) => {
    const e = computeLineEstimate(item, item.quantity);
    return e.hasEstimate ? sum + e.lineEstimate : sum;
  }, 0);
  const weighedEstimateCount = packagedItems.filter(
    item => computeLineEstimate(item, item.quantity).hasEstimate
  ).length;
  const hasWeighedEstimate = weighedEstimateCount > 0;
  const weighedEstimatePartial = hasWeighedEstimate && weighedEstimateCount < packagedItems.length;

  const handleQtyDecrement = (item) => {
    const step = isBulkMode(item) ? BULK_WEIGHT_STEP : 1;
    updateQuantity(item.itemId, Math.round((item.quantity - step) * 100) / 100);
  };

  const handleQtyIncrement = (item) => {
    const step = isBulkMode(item) ? BULK_WEIGHT_STEP : 1;
    updateQuantity(item.itemId, Math.round((item.quantity + step) * 100) / 100);
  };

  const handleQtyInput = (item, raw) => {
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    updateQuantity(item.itemId, parsed);
  };

  return (
    <div className="cart-page">
      <section className="cart-hero">
        <div className="cart-hero-content">
          <h1>Your Cart</h1>
        </div>
      </section>

      <section className="cart-content section">
        <div className="container">
          {cartItems.length === 0 ? (
            <div className="empty-cart">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
              <h2>Your cart is empty</h2>
              <p>Looks like you haven't added anything to your cart yet.</p>
              <Link to="/shopping" className="btn btn-primary btn-lg">
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="cart-grid">
              <div className="cart-items">
                <div className="cart-header">
                  <span className="cart-header-product">Product</span>
                  <span className="cart-header-price">Price</span>
                  <span className="cart-header-quantity">Quantity</span>
                  <span className="cart-header-total">Total</span>
                  <span className="cart-header-remove"></span>
                </div>

                {cartItems.map(item => {
                  const isPackage = isPackageMode(item);
                  const isBulk = isBulkMode(item);
                  const uom = unitLabel(item.unit_of_measure);
                  const est = computeLineEstimate(item, item.quantity);
                  const estLabel = lineEstimateLabel(item, item.quantity);
                  const bulkTotal = isBulk ? lineTotal(item) : null;
                  const minQty = isBulk ? BULK_WEIGHT_STEP : 1;

                  return (
                    <div key={item.itemId} className="cart-item">
                      <div className="cart-item-product">
                        <div className="cart-item-image">
                          <img src={resolveItemImageUrl(item.image) || PLACEHOLDER_IMAGE} alt={item.name} />
                        </div>
                        <div className="cart-item-info">
                          <h3>{item.name}</h3>
                          {item.variant && <p className="variant">{item.variant}</p>}
                          {isPackage && (
                            <p className="cart-item-weighed-tag">Priced by weight</p>
                          )}
                          {isBulk && (
                            <p className="cart-item-bulk-tag">Sold by weight</p>
                          )}
                        </div>
                      </div>

                      <div className="cart-item-price">
                        ${Number(item.price).toFixed(2)}
                        {(isPackage || isBulk) && (
                          <span className="cart-item-price-unit"> / {uom}</span>
                        )}
                      </div>

                      <div className="cart-item-quantity">
                        <button 
                          onClick={() => handleQtyDecrement(item)}
                          disabled={item.quantity <= minQty}
                        >
                          -
                        </button>
                        {isBulk ? (
                          <input
                            type="number"
                            className="cart-bulk-qty-input"
                            value={item.quantity}
                            min={BULK_WEIGHT_STEP}
                            step={BULK_WEIGHT_STEP}
                            onChange={(e) => handleQtyInput(item, e.target.value)}
                            aria-label={`Weight in ${uom}`}
                          />
                        ) : (
                          <span>{item.quantity}</span>
                        )}
                        <button onClick={() => handleQtyIncrement(item)}>
                          +
                        </button>
                        {isBulk && (
                          <span className="cart-bulk-uom">{uom}</span>
                        )}
                      </div>

                      <div className="cart-item-total">
                        {isPackage
                          ? (est.hasEstimate
                              ? <span className="cart-item-total-est" title={estLabel}>
                                  est. {formatMoney(est.lineEstimate)}
                                </span>
                              : <span className="cart-item-total-tbd">At weighing</span>)
                          : <span>{formatMoney(bulkTotal ?? Number(item.price) * item.quantity)}</span>}
                      </div>

                      <button 
                        className="cart-item-remove"
                        onClick={() => removeFromCart(item.itemId)}
                        aria-label="Remove item"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="cart-summary">
                <h2>Order Summary</h2>

                {hasDeterministic && (
                  <>
                    <div className="summary-row">
                      <span>Subtotal{hasPackaged ? ' (charged now)' : ''}</span>
                      <span>${estimatedSubtotal.toFixed(2)}</span>
                    </div>

                    <div className="summary-row">
                      <span>Shipping</span>
                      <span>Calculated at checkout</span>
                    </div>

                    <div className="summary-row">
                      <span>Tax</span>
                      <span>Calculated at checkout</span>
                    </div>

                    <div className="summary-total">
                      <span>Estimated Total{hasPackaged ? ' due now' : ''}</span>
                      <span>${estimatedSubtotal.toFixed(2)}</span>
                    </div>
                  </>
                )}

                {hasPackaged && (
                  <div className="cart-preorder-note">
                    <strong>{hasDeterministic ? 'Some items are' : 'This is a'} priced by weight</strong>
                    {hasWeighedEstimate && (
                      <div className="cart-preorder-estimate">
                        <span>Estimated weighed items{weighedEstimatePartial ? ' (partial)' : ''}</span>
                        <span>~{formatMoney(weighedEstimateTotal)}</span>
                      </div>
                    )}
                    <p>
                      {hasDeterministic
                        ? 'Your package items are placed as a pre-order. '
                        : 'Placing this order creates a pre-order. '}
                      We weigh them after you order, then email you a secure
                      payment link for the final amount.{' '}
                      {hasWeighedEstimate
                        ? 'The estimate above is based on typical weights and may change once weighed — '
                        : ''}
                      You aren't charged for package items{hasDeterministic ? ' now' : ' at checkout'}.
                    </p>
                  </div>
                )}

                {!hasDeterministic && (
                  <div className="summary-total">
                    <span>Due at checkout</span>
                    <span>$0.00</span>
                  </div>
                )}

                <Link to="/checkout" className="btn btn-primary btn-lg btn-checkout">
                  Proceed to Checkout
                </Link>

                <Link to="/shopping" className="continue-shopping">
                  ← Continue Shopping
                </Link>

                <div className="cart-note">
                  <p>
                    <strong>Members</strong> may receive discounted pricing.{' '}
                    <Link to="/frequently-asked-questions">Learn more</Link>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Cart;
